import { type FC, useCallback } from 'react';
import { FeatureIndicatorIcon } from '@components/FeatureIndicatorIcon';
import { Show } from '@components/Show';
import { BlockExpiryDateIndicator } from '@ContractBuilder/modules/block/components/BlockExpiryDateIndicator';
import { getLockedBlockPermissionsMessage } from '@ContractBuilder/utils';
import {
  mdiArrowDecisionAuto,
  mdiEye,
  mdiEyeOff,
  mdiFormatPageBreak,
  mdiLock,
  mdiPaletteSwatchVariant,
  mdiTuneVertical,
} from '@mdi/js';
import type { ResourceBlock } from '@root/@types/base';
import type { BlockVisibilityOutput } from '@root/@types/types';
import { isClauseBlock } from '@root/helpers/blocks';
import { isEndorsementViewPath, isTemplatePath } from '@root/src/utils/app-paths';
import clsx from 'clsx';

import { indicatorIconsClasses } from '../classes';
import { BlockPermissionsSet } from '../components/BlockPermissionsSet';
import { BlockUsedWithin } from '../components/BlockUsedWithin';
import { VisibilityReasons } from '../components/VisibilityReasons';

interface FeatureIndicatorsProps {
  block: ResourceBlock;
  hasVariationsConditionalLogic: boolean;
  isEnabled: boolean;
  isHiddenFromPDFPreview: boolean;
  isVisible: boolean;
  showLockedIndicator: boolean;
  showPageBreakIndicator: boolean;
  showPermissionsSetIndicator: boolean;
  showUsedInTemplatesControls: boolean;
  showVariationsIndicator: boolean;
  showVisibilityReasonsIndicatorIcon: boolean;
  variations: {
    count: number;
    selected: boolean;
    visibilityDetails: BlockVisibilityOutput[] | undefined;
  };
  visibilityDetails: BlockVisibilityOutput | undefined;
}

export const FeatureIndicators: FC<FeatureIndicatorsProps> = ({
  block,
  hasVariationsConditionalLogic,
  isEnabled,
  isHiddenFromPDFPreview,
  isVisible,
  showLockedIndicator,
  showPageBreakIndicator,
  showPermissionsSetIndicator,
  showUsedInTemplatesControls,
  showVariationsIndicator,
  showVisibilityReasonsIndicatorIcon,
  variations,
  visibilityDetails,
}) => {
  const isEndorsementView = isEndorsementViewPath();
  const isTemplate = isTemplatePath();

  const visibilityReasonsInner = useCallback(
    ({ onClick }: any) => (
      <FeatureIndicatorIcon
        path={isVisible ? mdiEye : mdiEyeOff}
        content={visibilityDetails?.info}
        data-testid={`block-icon-indicator:visibility-${block.id}`}
        isInteractive
        onClick={onClick}
        className={'text-info-400'}
      />
    ),
    [isVisible, visibilityDetails?.info, block.id],
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <div className={clsx(indicatorIconsClasses)}>
      <Show when={isTemplate}>
        <BlockExpiryDateIndicator
          expiryDate={block.expiry_date}
          inceptionDate={block.inception_date}
          isBlock={!isClauseBlock(block)}
        />
      </Show>
      <Show when={isEndorsementView && isHiddenFromPDFPreview}>
        <FeatureIndicatorIcon
          placement="top"
          path={mdiEyeOff}
          content="This block will be hidden in the PDF"
          data-testid="block-icon-indicator:hidden-in-pdf"
        />
      </Show>
      <Show when={!isEndorsementView}>
        <Show when={showVisibilityReasonsIndicatorIcon}>
          <VisibilityReasons visibilityDetails={visibilityDetails} trigger={visibilityReasonsInner} />
        </Show>
        <Show when={showVariationsIndicator}>
          <FeatureIndicatorIcon
            path={hasVariationsConditionalLogic ? mdiArrowDecisionAuto : mdiPaletteSwatchVariant}
            content={`${variations.count} variations`}
            data-testid="block-icon-indicator:variations"
          />
        </Show>
        <Show when={showPageBreakIndicator}>
          <FeatureIndicatorIcon
            path={mdiFormatPageBreak}
            content={`Page break after block`}
            data-testid="block-icon-indicator:page-break"
          />
        </Show>
        <Show when={showUsedInTemplatesControls}>
          <BlockUsedWithin usedInTemplates={block.usedInTemplates} />
        </Show>
        <Show when={showPermissionsSetIndicator}>
          <FeatureIndicatorIcon
            path={mdiTuneVertical}
            content={<BlockPermissionsSet block={block} />}
            data-testid="block-icon-indicator:permissions-set"
          />
        </Show>
        <Show when={showLockedIndicator}>
          <FeatureIndicatorIcon
            path={mdiLock}
            content={getLockedBlockPermissionsMessage(
              block.canEdit,
              block.canDelete,
              block.canEditOnTemplate,
              block.canDeleteOnTemplate,
              isTemplate,
            )}
            data-testid={`block-icon-indicator:${block.canDelete ? 'lock-edit' : 'lock-delete'}`}
          />
        </Show>
      </Show>
    </div>
  );
};
